import { createEffect, createSignal, onCleanup, Show } from "solid-js";
import { Icons } from "../components/Icons";
import { BalancePill } from "../components/BalancePill";
import { SlotMachine } from "../components/SlotMachine";
import { Confetti } from "../components/Confetti";
import { IdeaModal } from "../components/IdeaModal";
import { JackpotShield } from "../components/JackpotShield";
import {
  api,
  spinStream,
  type Competitor,
  type DiceRoll,
  type IdeaDTO,
  type Lang,
  type ProfileDTO,
  type SpinStreamEvent,
} from "../lib/api";
import {
  MODEL_LABELS,
  WILDNESS_LABELS,
  WILDNESS_POOL,
  TEMPERATURE_LABELS,
  TEMPERATURE_POOL,
} from "../lib/reels";
import { pickModel, modelIndex, MODELS, type ModelDef } from "../lib/models";
import { impact, notify } from "../lib/haptics";
import {
  isMuted,
  setMuted,
  playFinish,
  playAnticipation,
  startSpinLoop,
  stopSpinLoop,
} from "../lib/sound";
import { lang as i18nLang, t } from "../lib/i18n";
import { addRejected, listRejected } from "../lib/rejected";
import { pickVerbs } from "../lib/verbs";
import { demoSpinStream, isDemoMode } from "../lib/demo";
import { loadLastSpin, saveLastSpin } from "../lib/last-spin";

interface SlotsTabProps {
  profile: ProfileDTO | null;
  onSpinComplete?: (idea: IdeaDTO) => void;
  /** Fired when the worker refunds a spin (model_failed, etc.) so the
   *  parent can refetch /api/me and the balance pill reflects reality. */
  onSpinFailed?: () => void;
  /** Called when the user clicks SPIN with no free quota and no credits.
   *  The App owns the BuyCreditsModal state, SlotsTab just signals. */
  onNeedCredits: () => void;
}

type Program = { duration: number; speed: number };

const DEFAULT_PROGRAM: Program = { duration: 320, speed: 5500 };

// Reel timing budget. Stop windows are *minimum*, the third reel may stretch
// further if the LLM is slow (anticipation mode).
const REEL_TIMING = [
  { dur: [220, 320], spd: [6500, 8500], stop: [280, 380] },
  { dur: [280, 400], spd: [5500, 7500], stop: [480, 620] },
  { dur: [340, 480], spd: [4500, 6500], stop: [720, 900] },
] as const;

/** Minimum drama before reel 3 ever lands, feels intentional, not cheap. */
const MIN_DRAMA_MS = 900;
/** Soft cap. Past this we switch reel 3 into anticipation mode. */
const ANTICIPATION_AT_MS = 2200;
/** Hard cap. We land reel 3 with whatever fields we have.
 *  Worst case (validator-rejected primary + retry on Sonnet + Exa) we've
 *  measured: 55s. The anticipation animation hides any wait past 2.2s,
 *  so 60s gives a real safety margin and only fires when something is
 *  actually broken upstream (OpenRouter timeout, Exa down, etc). */
// Pair with the worker's 30 s OPENROUTER_TIMEOUT_MS. The worker aborts
// the upstream call first and emits a `model_failed` error with refund;
// our timer is just the belt-and-braces in case the worker itself
// gets stuck (CF Workers death, network blip).
const MAX_WAIT_MS = 35000;

function rand(min: number, max: number): number {
  return min + Math.random() * (max - min);
}
function pickProgram(reelIdx: number): Program {
  const { dur, spd } = REEL_TIMING[reelIdx];
  return {
    duration: Math.round(rand(dur[0], dur[1])),
    speed: Math.round(rand(spd[0], spd[1])),
  };
}
function pickStopDelays(): [number, number, number] {
  return [0, 1, 2].map((i) => {
    const [a, b] = REEL_TIMING[i].stop;
    return Math.round(rand(a, b));
  }) as [number, number, number];
}

function deferred<T>(): { promise: Promise<T>; resolve: (v: T) => void } {
  let resolve!: (v: T) => void;
  const promise = new Promise<T>((r) => (resolve = r));
  return { promise, resolve };
}

const sleep = (ms: number) =>
  new Promise<void>((r) => setTimeout(r, Math.max(0, ms)));

export function SlotsTab(props: SlotsTabProps) {
  // Hydrate reels + idea from the last completed spin so a returning user
  // sees their previous result instead of the default placeholder state.
  const last = loadLastSpin();

  const [reelSpinning, setReelSpinning] = createSignal<[boolean, boolean, boolean]>([
    false, false, false,
  ]);
  const [programs, setPrograms] = createSignal<[Program, Program, Program]>([
    DEFAULT_PROGRAM, DEFAULT_PROGRAM, DEFAULT_PROGRAM,
  ]);
  const [finalIndices, setFinalIndices] = createSignal<[number, number, number]>(
    last?.finalIndices ?? [0, 0, 0],
  );

  const [modalOpen, setModalOpen] = createSignal(false);
  const [currentIdea, setCurrentIdea] = createSignal<IdeaDTO | null>(last?.idea ?? null);
  const [partialIdea, setPartialIdea] = createSignal<Partial<IdeaDTO>>({});
  const [dice, setDice] = createSignal<DiceRoll | null>(null);
  const [competitors, setCompetitors] = createSignal<Competitor[]>([]);
  /** `pending` while we still expect competitors; `done` once they arrive or
   * the stream finishes without them. Drives the modal's loading placeholder. */
  const [competitorsState, setCompetitorsState] = createSignal<"pending" | "done">("pending");
  const [jackpot, setJackpot] = createSignal(false);
  const [confettiTrigger, setConfettiTrigger] = createSignal(0);
  const [shakeKey, setShakeKey] = createSignal(0);
  const [saving, setSaving] = createSignal(false);
  const [errorMsg, setErrorMsg] = createSignal<string | null>(null);
  const [muted, setMutedState] = createSignal(isMuted());
  const [anticipate, setAnticipate] = createSignal(false);
  const [cinema, setCinema] = createSignal(false);
  // Rotating "thinking" verb shown on the spin button while we wait for
  // reels + the LLM (otherwise the button stays static for the round-trip).
  const [spinVerb, setSpinVerb] = createSignal<string | null>(null);

  let timers: ReturnType<typeof setTimeout>[] = [];
  let activeAbort: AbortController | null = null;
  const clearAllTimers = () => {
    timers.forEach(clearTimeout);
    timers = [];
  };
  onCleanup(() => {
    clearAllTimers();
    stopSpinLoop();
    activeAbort?.abort();
  });

  // `cinema-mode` is toggled on <body> for the jackpot grade; ensure it’s
  // cleared if the tab unmounts mid‑spin (onMount returns are ignored in
  // Solid, must use onCleanup).
  onCleanup(() => document.body.classList.remove("cinema-mode"));

  // Rotate the spin button verb every ~900ms while any reel is spinning.
  createEffect(() => {
    if (!anySpinning()) {
      setSpinVerb(null);
      return;
    }
    const verbs = pickVerbs(8);
    let i = 0;
    setSpinVerb(verbs[0]);
    const handle = setInterval(() => {
      i = (i + 1) % verbs.length;
      setSpinVerb(verbs[i]);
    }, 900);
    onCleanup(() => clearInterval(handle));
  });

  const landReel = (idx: 0 | 1 | 2, h: Parameters<typeof impact>[0]): void => {
    setReelSpinning((r) => {
      const next: [boolean, boolean, boolean] = [...r];
      next[idx] = false;
      return next;
    });
    setShakeKey((k) => k + 1);
    impact(h);
  };

  const anySpinning = () => reelSpinning().some(Boolean);

  /**
   * Closing the modal while the spin is still in flight cancels everything
   * cleanly, stream aborted, reels landed (so they're not stuck), timers
   * cleared. Without this the lever stays disabled forever if the user taps
   * outside the sheet during streaming.
   *
   * If the idea is already in hand we just hide the sheet.
   */
  const cancelSpin = (): void => {
    activeAbort?.abort();
    activeAbort = null;
    clearAllTimers();
    stopSpinLoop();
    setReelSpinning([false, false, false]);
    setAnticipate(false);
    setCinema(false);
    document.body.classList.remove("cinema-mode");
  };

  const onModalClose = (): void => {
    setModalOpen(false);
    if (!currentIdea()) cancelSpin();
  };

  const handleSpin = async (): Promise<void> => {
    if (anySpinning()) return;
    // Refuse a new spin while the previous result sheet is still open,
    // prevents a double-stream race and stops the spin-loop sound from
    // restarting over the visible results.
    if (modalOpen()) return;
    // Credit gate. We trust the cached profile here; the worker still
    // re-checks and returns 402 if we're wrong (e.g. another device burned
    // the last spin). `free_limit === 0` means demo mode, skip the gate.
    const p = props.profile;
    if (p && p.free_limit > 0 && p.free_left === 0 && p.credits === 0) {
      impact("light");
      props.onNeedCredits();
      return;
    }
    clearAllTimers();
    activeAbort?.abort();
    setErrorMsg(null);
    setModalOpen(false);
    setCurrentIdea(null);
    setPartialIdea({});
    setDice(null);
    setCompetitors([]);
    setCompetitorsState("pending");
    setJackpot(false);
    setAnticipate(false);
    setCinema(false);
    document.body.classList.remove("cinema-mode");
    impact("medium");

    const spinStart = performance.now();
    const elapsed = () => performance.now() - spinStart;

    // Buffered streaming state. While the reels are still spinning we do
    // NOT push field events into the modal, they sit in a queue and are
    // replayed with a small stagger once the modal opens. That gives the
    // user a visible "streaming" effect inside the sheet even when the
    // LLM finished before reel 3 landed.
    let modalArmed = false;
    const fieldQueue: Array<{ key: keyof IdeaDTO; value: any }> = [];
    let pendingFullIdea: IdeaDTO | null = null;
    let pendingCompetitors: Competitor[] | null = null;

    // ── Pick all three parameters up front ──────────────────────────────
    // Pool follows the worker's accounting (free spins consumed before
    // paid credits). Picking from the wrong pool here causes the worker
    // to silently re-roll and the modal then shows a different model
    // than the one the reel landed on.
    const usingPaid =
      !!p && p.free_limit > 0 && p.free_left <= 0 && p.credits > 0;
    const clientPool: "full" | "free" = usingPaid ? "full" : "free";
    const picked: ModelDef = pickModel(clientPool);
    const tIdx = modelIndex(picked.label);
    const wildIdx = Math.floor(Math.random() * WILDNESS_POOL.length);
    const tempIdx = Math.floor(Math.random() * TEMPERATURE_POOL.length);
    const wildness = WILDNESS_POOL[wildIdx];
    const temperature = TEMPERATURE_POOL[tempIdx];

    const stopAt = pickStopDelays();

    setPrograms([pickProgram(0), pickProgram(1), pickProgram(2)]);
    setFinalIndices([tIdx, wildIdx, tempIdx]);
    setReelSpinning([true, true, true]);
    startSpinLoop();

    const lang: Lang = i18nLang() === "ru" ? "ru" : "en";

    const ideaReady = deferred<{ idea: IdeaDTO; tier: string }>();
    let streamErrored = false;

    // Reel 1 lands when meta is in AND the slot has spun long enough to
    // feel intentional. Floor = 1.5 s so the user always sees at least
    // one full reel-pass before the modal takes over the screen.
    // Streaming-started exception is intentionally NOT here: we want the
    // slot to feel mechanical even on fast LLM responses.
    // The thinking modal opens THE MOMENT reel 1 lands, not before,
    // so the user gets the satisfying click-of-the-first-reel before
    // the sheet slides up over the rest.
    const REEL0_MIN_MS = 1500;
    let reel0LandScheduled = false;
    let reel0MinDelayElapsed = false;
    let reel0MetaReceived = false;
    const maybeLandReel0 = () => {
      if (reel0LandScheduled) return;
      if (!reel0MetaReceived) return;
      if (!reel0MinDelayElapsed) return;
      reel0LandScheduled = true;
      landReel(0, "light");
      // Open the thinking modal right after reel 1's click. Flush any
      // buffered fields so partialIdea picks them up immediately.
      if (!streamErrored) {
        setModalOpen(true);
        modalArmed = true;
        for (const f of fieldQueue) {
          setPartialIdea((p) => ({ ...p, [f.key]: f.value }));
        }
        fieldQueue.length = 0;
      }
    };
    timers.push(
      setTimeout(() => {
        reel0MinDelayElapsed = true;
        maybeLandReel0();
      }, REEL0_MIN_MS),
    );
    // Safety net: if meta never arrives (network stall, validator retry),
    // land reel 1 on the client pre-pick after the soft cap so the slot
    // doesn't look frozen.
    timers.push(
      setTimeout(() => {
        reel0MetaReceived = true;
        maybeLandReel0();
      }, ANTICIPATION_AT_MS),
    );
    // Reels 2 & 3 are now purely client-side parameters, land them
    // independently of the LLM so the spin always feels mechanical.
    timers.push(setTimeout(() => landReel(1, "medium"), stopAt[1]));

    // Anticipation watcher: if no idea after the soft cap, slow reel 3.
    timers.push(
      setTimeout(() => {
        if (currentIdea() || streamErrored) return;
        if (!reelSpinning()[2]) return;
        setAnticipate(true);
        playAnticipation(1100);
      }, ANTICIPATION_AT_MS),
    );

    // Hard cap, bail out gracefully if the worker is dying. We refetch
    // the profile so the balance pill refreshes; the worker's own
    // timeout (OPENROUTER_TIMEOUT_MS=30 s) usually wins and refunds
    // server-side first, but if the worker itself died we still want
    // the UI to recover.
    timers.push(
      setTimeout(() => {
        if (currentIdea() || streamErrored) return;
        activeAbort?.abort();
        streamErrored = true;
        clearAllTimers();
        stopSpinLoop();
        setReelSpinning([false, false, false]);
        setModalOpen(false);
        setAnticipate(false);
        setCinema(false);
        document.body.classList.remove("cinema-mode");
        setErrorMsg(t("slots.error"));
        notify("error");
        props.onSpinFailed?.();
      }, MAX_WAIT_MS),
    );

    // (Modal opens inside maybeLandReel0() so the user always gets to
    // see reel 1 click before the sheet slides up.)

    // Stream the idea from the worker. Field events drive the live preview.
    activeAbort = new AbortController();
    // Compact summary of everything the user thumbs-downed in the past, the
    // worker injects this as a "do NOT repeat this shape" prompt block.
    // We cap at 5 entries and drop subtitle/reason so the system prompt stays
    // lean even after the user has rejected dozens of ideas.
    const rejected = listRejected()
      .slice(0, 5)
      .map((r) => ({ title: r.title }));
    const stream = isDemoMode()
      ? demoSpinStream({ language: lang, signal: activeAbort.signal })
      : spinStream({
          language: lang,
          model: picked.id,
          wildness: wildness.id,
          temperature: temperature.value,
          rejected,
          signal: activeAbort.signal,
        });

    const consumeStream = async () => {
      try {
        for await (const ev of stream as AsyncIterable<SpinStreamEvent>) {
          if (streamErrored) return;
          if (ev.type === "meta") {
            const m = MODELS.find((x) => x.id === ev.model);
            if (m) {
              setFinalIndices((f) => [modelIndex(m.label), f[1], f[2]] as [number, number, number]);
            }
            if (ev.dice) setDice(ev.dice);
            reel0MetaReceived = true;
            maybeLandReel0();
          } else if (ev.type === "field") {
            const key = ev.key as keyof IdeaDTO;
            if (modalArmed) {
              setPartialIdea((p) => ({ ...p, [key]: ev.value as any }));
            } else {
              // Buffer until reels land + modal opens.
              fieldQueue.push({ key, value: ev.value });
            }
          } else if (ev.type === "idea") {
            pendingFullIdea = ev.idea;
            ideaReady.resolve({ idea: ev.idea, tier: ev.idea.tier });
          } else if (ev.type === "competitors") {
            if (modalArmed) {
              setCompetitors(ev.competitors ?? []);
              setCompetitorsState("done");
            } else {
              pendingCompetitors = ev.competitors ?? [];
            }
          } else if (ev.type === "done") {
            // Belt-and-braces: if stream ends without an explicit competitors
            // event (Exa misconfigured / down), drop the loading state.
            setCompetitorsState("done");
          } else if (ev.type === "error") {
            streamErrored = true;
            clearAllTimers();
            stopSpinLoop();
            setReelSpinning([false, false, false]);
            setAnticipate(false);
            setCinema(false);
            document.body.classList.remove("cinema-mode");
            // Close the thinking modal, if we opened it (after reel 1
            // landed) we'd otherwise leave the user staring at a frozen
            // "Alex думает" while the error toast lives behind it.
            setModalOpen(false);
            modalArmed = false;
            setPartialIdea({});
            setCurrentIdea(null);
            if (ev.code === "no_credits") {
              // Worker says we're out, silent abort, open buy modal.
              activeAbort?.abort();
              props.onNeedCredits();
              return;
            }
            if (ev.code === "model_failed") {
              // The picked model failed and the worker refunded the spin.
              // Tell the user honestly which model flaked and ask the
              // parent to refresh the balance so the pill reflects the
              // refund immediately.
              const failed = (ev as { model_label?: string }).model_label ?? "the model";
              setErrorMsg(t("slots.modelFailed", { model: failed }));
              notify("error");
              props.onSpinFailed?.();
              return;
            }
            setErrorMsg(ev.code === "rate_limited" ? t("slots.rateLimited") : t("slots.error"));
            notify("error");
            return;
          }
        }
        setCompetitorsState("done");
      } catch (err: any) {
        if (err?.name === "AbortError") return;
        if (streamErrored) return;
        streamErrored = true;
        clearAllTimers();
        stopSpinLoop();
        setReelSpinning([false, false, false]);
        setErrorMsg(t("slots.error"));
        notify("error");
      }
    };
    void consumeStream();

    // Reel 3 lands only after both the minimum drama AND the idea are ready.
    const r3Trigger = Promise.all([
      Promise.race([ideaReady.promise, sleep(MAX_WAIT_MS - 200)]),
      sleep(stopAt[2]),
    ]);
    void r3Trigger.then(async () => {
      if (streamErrored) return;
      const result = await Promise.race([
        ideaReady.promise,
        sleep(MAX_WAIT_MS - elapsed()).then(() => null),
      ]);
      if (streamErrored) return;
      if (!result) return;
      const { idea, tier } = result;
      const isJackpot = tier === "premium" || tier === "joker";

      if (isJackpot) {
        setCinema(true);
        document.body.classList.add("cinema-mode");
        await sleep(600);
      }

      const minDrama = Math.max(0, MIN_DRAMA_MS - elapsed());
      const stopWindow = Math.max(0, stopAt[2] - elapsed());
      await sleep(Math.max(minDrama, stopWindow));
      if (streamErrored) return;

      setAnticipate(false);
      landReel(2, "heavy");
      stopSpinLoop();

      setJackpot(isJackpot);
      setConfettiTrigger((n) => n + 1);
      playFinish();
      if (isJackpot) {
        notify("success");
        timers.push(setTimeout(() => impact("heavy"), 120));
      } else {
        impact("light");
      }

      // Modal is already open in thinking mode (opened at click+200 ms).
      // Now that the idea is ready, commit it so the modal transitions
      // from ThinkingAnimation → full content. Any leftover buffered
      // fields (shouldn't happen, modal opened early flushes them) get
      // applied before the final commit so partials don't briefly show
      // stale state.
      for (const f of fieldQueue) {
        setPartialIdea((p) => ({ ...p, [f.key]: f.value }));
      }
      fieldQueue.length = 0;
      if (pendingFullIdea) setCurrentIdea(pendingFullIdea);
      if (pendingCompetitors) {
        setCompetitors(pendingCompetitors);
        setCompetitorsState("done");
      }

      // Remember the result so the next mount restores reels + idea.
      saveLastSpin({ idea, finalIndices: finalIndices() });

      timers.push(
        setTimeout(() => {
          setCinema(false);
          document.body.classList.remove("cinema-mode");
        }, isJackpot ? 1400 : 600),
      );

      props.onSpinComplete?.(idea);
    });
  };

  const handleToggleSave = async (): Promise<void> => {
    const idea = currentIdea();
    if (!idea) return;
    setSaving(true);
    try {
      if (idea.saved) {
        await api.unsave(idea.id);
        setCurrentIdea({ ...idea, saved: false });
      } else {
        await api.save(idea);
        setCurrentIdea({ ...idea, saved: true });
        impact("light");
      }
    } catch {
      /* ignore */
    } finally {
      setSaving(false);
    }
  };

  /**
   * "👎 Забраковать" handler. Persists the current idea to localStorage so
   * future spins can pass it to the worker as a "don't generate anything
   * like this" instruction. Closes the modal afterwards.
   */
  const handleReject = (): void => {
    const idea = currentIdea();
    if (!idea) return;
    addRejected({
      title: idea.title,
      subtitle: idea.subtitle,
    });
    impact("medium");
    setModalOpen(false);
  };

  const toggleMute = (): void => {
    const next = !muted();
    setMuted(next);
    setMutedState(next);
  };

  return (
    <div class="flex min-h-full flex-col">
      {/* 3-col grid keeps the wordmark optically centred regardless of
         left/right slot width; hairline border-bottom separates the header
         from the slot machine. */}
      <header
        style={{
          display: "grid",
          "grid-template-columns": "1fr auto 1fr",
          "align-items": "center",
          padding: "calc(var(--app-safe-top) + 12px) 16px 12px",
          "border-bottom": "1px solid rgba(184, 146, 63, 0.18)",
          background: "#fffefb",
          "flex-shrink": 0,
        }}
      >
        {/* Left slot, balance pill with tap-to-explain popover. Hidden
            in demo (free_limit === 0) so the chrome stays clean. */}
        <Show
          when={props.profile && props.profile.free_limit > 0}
          fallback={<div style={{ "justify-self": "start", height: "24px" }} />}
        >
          <BalancePill
            freeLeft={props.profile!.free_left}
            freeLimit={props.profile!.free_limit}
            credits={props.profile!.credits}
            onBuy={() => props.onNeedCredits()}
          />
        </Show>

        <div
          class="serif"
          style={{
            "font-size": "20px",
            "font-weight": 600,
            "letter-spacing": "-0.3px",
            color: "#1a1a1a",
            "line-height": 1,
          }}
        >
          saas
          <span style={{ color: "#b8923f" }}>lots</span>
          <span style={{ color: "#9a9a9a", "font-size": "14px", "font-weight": 500 }}>
            {t("slots.brandSuffix")}
          </span>
        </div>

        <button
          onClick={toggleMute}
          aria-label={muted() ? t("common.unmute") : t("common.mute")}
          aria-pressed={muted()}
          style={{
            "justify-self": "end",
            display: "inline-flex",
            "align-items": "center",
            "justify-content": "center",
            // Flat: no background, no border. Padding gives a 44pt hit
            // target without inflating the visual footprint.
            padding: "10px 4px",
            "min-height": "44px",
            background: "transparent",
            border: 0,
            color: muted() ? "#9a9a9a" : "#1a1a1a",
            "font-size": "12px",
            "font-weight": 700,
            "letter-spacing": "1px",
            "text-transform": "uppercase",
            cursor: "pointer",
            "font-family": "inherit",
          }}
        >
          {muted() ? (
            <Icons.SoundOff size={18} color="#9a9a9a" />
          ) : (
            <Icons.SoundOn size={18} color="#b8923f" />
          )}
        </button>
      </header>

      <div style={{ padding: "18px 20px 20px", position: "relative" }}>
        <SlotMachine
          reels={[MODEL_LABELS, WILDNESS_LABELS, TEMPERATURE_LABELS]}
          finalIndices={finalIndices()}
          reelSpinning={reelSpinning()}
          programs={programs()}
          anySpinning={anySpinning()}
          onLeverPull={handleSpin}
          shakeKey={shakeKey()}
          jackpot={jackpot() && modalOpen()}
          anticipate={anticipate()}
          kinds={["logo", "icon-label", "icon-label"]}
        />
        <Show when={jackpot() && cinema()}>
          <div class="bloom-overlay on" />
        </Show>
        <JackpotShield
          show={jackpot() && cinema()}
          modelLabel={MODEL_LABELS[finalIndices()[0]] ?? ""}
        />
      </div>

      <div style={{ padding: "4px 20px 12px" }}>
        <button
          onClick={handleSpin}
          disabled={anySpinning()}
          class={anySpinning() ? "" : "spin-glow"}
          style={{
            width: "100%",
            // 60 → 64 so the touch target clears 44pt + comfy margin.
            height: "64px",
            "border-radius": "32px",
            border: "none",
            background: anySpinning()
              ? "linear-gradient(180deg, #e3d5a8 0%, #c9b078 100%)"
              : "linear-gradient(180deg, #e8d4a2 0%, #c9a961 50%, #b8923f 100%)",
            // Dark ink on gold gradient passes AA easily (was white on gold,
            // 1.04 ratio, QA P1 #12). Subtle cream relief keeps the
            // "engraved metal" look.
            color: "#1a1a1a",
            "font-family": "inherit",
            "font-size": anySpinning() ? "16px" : "19px",
            "font-weight": 700,
            "letter-spacing": anySpinning() ? "1.2px" : "6px",
            "text-transform": anySpinning() ? "uppercase" : "none",
            cursor: anySpinning() ? "default" : "pointer",
            position: "relative",
            "text-shadow": "0 1px 0 rgba(255, 245, 208, 0.55)",
            transition: "transform 0.15s ease",
            transform: anySpinning() ? "translateY(1px) scale(0.99)" : "none",
            "box-shadow": anySpinning()
              ? "0 4px 12px rgba(120, 95, 40, 0.18)"
              : "0 6px 14px rgba(120, 95, 40, 0.25), 0 14px 28px rgba(120, 95, 40, 0.18)",
          }}
        >
          {anySpinning()
            ? spinVerb()
              ? `${spinVerb()}…`
              : t("slots.spinning")
            : t("slots.spin")}
        </button>
      </div>

      <Show when={errorMsg()}>
        <div
          class="mx-auto"
          style={{
            margin: "0 20px",
            padding: "10px 14px",
            background: "rgba(195,51,51,0.08)",
            border: "1px solid rgba(195,51,51,0.3)",
            "border-radius": "14px",
            color: "#a32a2a",
            "font-size": "13px",
            "margin-bottom": "12px",
          }}
        >
          {errorMsg()}
        </div>
      </Show>

      <div class="flex-1" style={{ "min-height": "12px" }} />

      <Confetti trigger={confettiTrigger()} intensity={jackpot() ? "burst" : "small"} />
      <IdeaModal
        idea={currentIdea()}
        partial={partialIdea()}
        dice={dice()}
        competitors={competitors()}
        competitorsLoading={competitorsState() === "pending"}
        open={modalOpen()}
        thinkingModel={MODEL_LABELS[finalIndices()[0]] ?? ""}
        onClose={onModalClose}
        onToggleSave={handleToggleSave}
        onReject={handleReject}
        saving={saving()}
        jackpot={jackpot()}
      />
    </div>
  );
}
